<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
        <modal
            :id="modalId"
            title="Edit Property"
            ok-title="Save"
            :ok-disabled="!meta.valid"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
        >
            <busy-modal :show="loading" :overlay="true"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row>
                    <b-col>
                        <validation-provider
                            :rules="values.update_lockstate_property ? 'required' : ''"
                            v-slot="{ errors }"
                            
                        >
                            <form-select
                                label="Lodgix property:"
                                v-model="values.rental_property_id"
                                :options="propertyOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Time zone:"
                            v-model="values.time_zone"
                            :options="timeZoneOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button-group :style="{ width: '100%' }">
                            <form-select
                                :style="{ display: '-webkit-inline-box', width: '90%'}"
                                label="Lock:"
                                v-model="values.lock_id"
                                :options="lockOptions"
                            ></form-select>
                            <responsive-button
                                class="action-button mb-3"
                                :style="{marginRight: '0px !important'}"
                                :icon="['fas', 'arrows-rotate']"
                                variant="primary"
                                @click="handleFetchLocksClick"
                            ></responsive-button>
                            <responsive-button
                                class="action-button mb-3"
                                :style="{marginRight: '0px !important'}"
                                :icon="['fas', 'plus']"
                                variant="primary"
                                @click="handleAddLockClick"
                            ></responsive-button>
                        </b-button-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            label="Update LockState property?"
                            v-model="values.update_lockstate_property"
                            :options="updateLocsOptions"
                        ></form-select>
                    </b-col>
                </b-row>
            </b-form>
            <resort-lock-lock-detail-modal-form
                :modal-id="addResortLockModalID"
                :create="createResortLockLock"
                @success-save="handleCreateResortLockLockSuccess"
            />
        </modal>
    </validation-observer>
</template>
<script>
    import API from '../../../../api';
    import isEmpty from 'lodash/isEmpty';
    import {
        DOOR_LOCKS_RESORT_LOCK_PROPERTY_OPTIONS_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_FETCH_LOCKS_ENDPOINT,
        DOOR_LOCKS_TIMEZONES_ENDPOINT,
    } from '../../../../endpoints';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import { formatResponseError } from '../../../../utils/api';
    import ResortLockLockDetailModalForm from './resort-lock-lock/resort-lock-lock-detail-modal-form.vue';

    export default {
        name: 'ResortLockPropertyDetailModalForm',
        components: {
            BusyModal,
            Modal,
            FormSelect,
            ResponsiveButton,
            ResortLockLockDetailModalForm,
        },
        props: {
            modalId: String,
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                loading: false,
                values: null,
                addResortLockModalID: 'resort-lock-add-lock-modal',
                propertyOptions: [],
                lockOptions: [],
                timeZoneOptions: [],
                updateLocsOptions: [
                    { value: false, text: 'No' },
                    { value: true, text: 'Yes' },
                ],
            }
        },
        methods: {
            getInitialValues() {
                return {
                    id: this.initialValues.id || null,
                    rental_property_id: this.initialValues.rental_property_id || null,
                    time_zone: this.initialValues.time_zone || null,
                    lock_id: this.initialValues.lock_id || null,
                    update_lockstate_property: false,
                }
            },
            handleAddLockClick() {
                this.$bvModal.show(this.addResortLockModalID);
            },
            async handleCreateResortLockLockSuccess({ id }) {
                await this.fetchLockOptions();
                this.values.lock_id = id;
            },
            async handleFetchLocksClick() {
                this.loading = true;

                try {
                    await this.fetchLocks();
                    await this.fetchLockOptions();
                } finally {
                    this.loading = false;
                }
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            async load() {
                this.loading = true;

                try {
                    await Promise.all([
                        this.fetchTimeZoneOptions(),
                        this.fetchLodgixPropertyOptions(),
                        this.fetchLockOptions(),
                    ])
                } finally {
                    this.loading = false;
                }
            },
            async fetchLodgixPropertyOptions() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_PROPERTY_OPTIONS_ENDPOINT(this.initialValues.id))
                    this.propertyOptions = [
                        { value: null, text: 'Not set'},
                        ...response.data.map(property => ({
                            value: property.id,
                            text: property.name
                        }))
                    ]
                } catch {
                    this.errorToast({ text: 'Could not load lodgix properties.'})
                }
            },
            async fetchTimeZoneOptions() {
                try {
                    const response = await API.get(DOOR_LOCKS_TIMEZONES_ENDPOINT());
                    this.timeZoneOptions = response.data.map(t => ({ value: t.name, text: t.name }));
                } catch (err) {
                    this.errorToast({ text: 'Could not load time zones.'})
                }
            },
            async fetchLockOptions() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT(this.initialValues.id));
                    this.lockOptions = [
                        { value: null, text: 'Not set'},
                        ...response.data.map(lock => ({
                            value: lock.id,
                            text: lock.name
                        }))
                    ]
                } catch {
                    this.errorToast({ text: 'Could not load locks.'})
                }
            },
            async fetchLocks() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_FETCH_LOCKS_ENDPOINT(this.initialValues.id));
                    const count = response.data.count;
                    this.successToast({text: `Fetched ${count} ${count === 1 ? 'lock' : 'locks'}.`})
                } catch {
                    this.errorToast({ text: 'Could not fetch locks.'})
                }

            },
            createResortLockLock(values) {
                return API.post(DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT(this.initialValues.id), values);
            },
            async save() {
                this.loading = true;  
             
                try { 
                    await this.update(this.values);
                    this.$emit('success-save');
                    this.$nextTick(() => {
                        this.hideModal();
                    })
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not save resort lock property'})
                } finally {
                    this.loading = false;
                }
            },
        },
        watch: {
            async initialValues() {
                this.values = null;
                if (!isEmpty(this.initialValues)) {
                    await this.load();
                }
                this.values = this.getInitialValues();
            },
        }
    }
</script>

<style lang="scss" scoped>
    .action-button {
        height: 2.4rem !important;
        align-self: flex-end;
    }
</style>
